import React, { useState, useEffect } from 'react';
import polygonIcon from 'static/images/polyIcon.png';
import bscIcon from 'static/images/bscChain.png';
import ethIcon from 'static/images/etherChain.png';
import zetaIcon from 'static/images/zetaIcon.png';
import defaultIcon from 'static/images/defaultChain.png';
import { Box, Button, IconButton, Popper, Paper } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ethers } from 'ethers';
import './WalletConnectButtons.css';
import MetaMaskIcon from 'static/images/metamask_icon.webp';

const WalletConnectButtons = () => {
  const [walletAddress, setWalletAddress] = useState('');
  const [chainName, setChainName] = useState('');
  const [chainIcon, setChainIcon] = useState(defaultIcon);
  const [anchorElBuySell, setAnchorElBuySell] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const isOpenBuySell = Boolean(anchorElBuySell);
  const handleBuySellClick = (event) => {
    setAnchorElBuySell(isOpenBuySell ? null : event.currentTarget);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const openMetaMaskBuy = () => {
    setAnchorElBuySell(null);
    window.open('https://portfolio.metamask.io/buy/build-quote', '_blank');
  };

  const openMetaMaskSell = () => {
    setAnchorElBuySell(null);
    window.open('https://portfolio.metamask.io/sell/build-quote', '_blank');
  };

  const updateWalletInfo = async () => {
    if (window.ethereum !== undefined) {
      try {
        // Chỉ kiểm tra tài khoản đã kết nối, không tự động yêu cầu kết nối
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
        } else {
          setWalletAddress('');
        }

        // Tạo provider và lấy thông tin mạng
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const network = await provider.getNetwork();

        // Cập nhật thông tin mạng
        switch (network.chainId) {
          case 1:
            setChainName('Ethereum');
            setChainIcon(ethIcon);
            break;
          case 137:
            setChainName('Polygon');
            setChainIcon(polygonIcon);
            break;
          case 56:
            setChainName('Binance Smart Chain');
            setChainIcon(bscIcon);
            break;
          case 7001:
            setChainName('Zeta Testnet');
            setChainIcon(zetaIcon);
            break;
          default:
            setChainName('Unknown');
            setChainIcon(defaultIcon);
        }
      } catch (error) {
        console.error('Error fetching wallet info:', error);
      }
    } else {
      alert('Please install MetaMask to use this feature');
    }
  };

  // Hàm kết nối MetaMask và chuyển mạng nếu cần
  const connectMetamask = async (chainId) => {
    if (window.ethereum !== undefined) {
      try {
        // Yêu cầu kết nối MetaMask
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
        }

        // Chuyển mạng nếu cần
        if (chainId) {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: `0x${chainId.toString(16)}` }],
          });
        }

        // Cập nhật lại ví và mạng
        updateWalletInfo();
      } catch (error) {
        console.error(error);
      }
    } else {
      alert('Please install MetaMask to use this feature');
    }
  };

  // Mở menu lựa chọn mạng
  const handleMenuClick = (event) => {
    setAnchorElBuySell(null);
    setAnchorEl(event.currentTarget);
  };

  // Đóng menu và kết nối mạng tương ứng
  const handleMenuClose = (chainId) => {
    setAnchorEl(null);
    if (chainId) {
      connectMetamask(chainId);
    }
  };

  // Định dạng địa chỉ ví để hiển thị ngắn gọn
  const formatWalletAddress = (address) => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Lắng nghe sự kiện khi địa chỉ ví thay đổi và mạng thay đổi
  useEffect(() => {
    // Kiểm tra ngay khi component được mount
    updateWalletInfo();

    // Lắng nghe sự kiện khi địa chỉ ví thay đổi
    const handleAccountsChanged = (accounts) => {
      setWalletAddress(accounts[0]);
      updateWalletInfo();
    };

    // Lắng nghe sự kiện khi mạng thay đổi
    const handleChainChanged = (chainId) => {
      updateWalletInfo();
    };

    // Đăng ký các sự kiện của MetaMask
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }

    // Dọn dẹp khi component unmount
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []);

  return (
    <Box className="wallet-connect-buttons">
      <Box className="chakra-stack">
        {/* Hiển thị thông tin mạng nếu đã kết nối */}
        {walletAddress && (
          <Box className="css-1glc979 chain-info">
            <img src={chainIcon} alt={chainName} className="chakra-image" />
            <span className="chain-name">{chainName}</span>
          </Box>
        )}

        {/* Nút kết nối ví hoặc hiển thị địa chỉ ví */}
        <Box className="css-1abwygc connect-button-box">
          <Button
            type="button"
            className="chakra-button css-jqvm2u"
            aria-label="select-network"
            onClick={walletAddress ? handleMenuClick : () => connectMetamask()}
          >
            {walletAddress ? formatWalletAddress(walletAddress) : 'Connect'}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: 'black', // Set menu background to black
                color: 'white', // Set text to white
              },
            }}
          >
            <MenuItem
              onClick={() => handleMenuClose(137)} // Chọn Polygon
              sx={{
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#555',
                },
              }}
            >
              <img
                src={polygonIcon}
                alt="Polygon"
                style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '8px',
                }}
              />
              Polygon
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuClose(56)} // Chọn Binance Smart Chain
              sx={{
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#555',
                },
              }}
            >
              <img
                src={bscIcon}
                alt="Binance Smart Chain"
                style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '8px',
                }}
              />
              Binance Smart Chain
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuClose(1)} // Chọn Ethereum
              sx={{
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#555',
                },
              }}
            >
              <img
                src={ethIcon}
                alt="Ethereum"
                style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '8px',
                }}
              />
              Ethereum
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuClose(7001)} // Chọn Zeta Testnet
              sx={{
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#555',
                },
              }}
            >
              <img
                src={zetaIcon}
                alt="Zeta Testnet"
                style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '8px',
                }}
              />
              Zeta Testnet
            </MenuItem>
          </Menu>
          <Box className="connect-button-box" sx={{ position: 'relative' }}>
            {/* Nút MetaMask */}
            <IconButton onClick={handleBuySellClick}>
              <img src={MetaMaskIcon} alt="MetaMask" style={{ width: '40px', height: '40px' }} />
            </IconButton>

            {/* Popper chứa các nút Buy & Sell */}
            <Popper open={isOpenBuySell} anchorEl={anchorElBuySell} placement="bottom-start" disablePortal>
              <Paper
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  p: 1,
                  gap: 1,
                  mt: 1,
                  backgroundColor: 'black', // Nền đen cho Paper
                }}
              >
                <Button
                  variant="contained"
                  onClick={openMetaMaskBuy}
                  startIcon={<img src={MetaMaskIcon} alt="Buy" style={{ width: 20, height: 20 }} />}
                  sx={{
                    backgroundColor: 'black',
                    color: 'white',
                    border: '1px solid white', // Viền trắng
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    '&:hover': { backgroundColor: '#333', border: '1px solid #ddd' }, // Viền xám nhạt khi hover
                  }}
                  fullWidth
                >
                  Buy
                </Button>

                <Button
                  variant="contained"
                  onClick={openMetaMaskSell}
                  startIcon={<img src={MetaMaskIcon} alt="Sell" style={{ width: 20, height: 20 }} />}
                  sx={{
                    backgroundColor: 'black',
                    color: 'white',
                    border: '1px solid white', // Viền trắng
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    '&:hover': { backgroundColor: '#333', border: '1px solid #ddd' }, // Viền xám nhạt khi hover
                  }}
                  fullWidth
                >
                  Sell
                </Button>
              </Paper>
            </Popper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WalletConnectButtons;
